/**
 * Shared access-grant / assignee-pool matching (user | role | unit | title).
 */

const GRANT_TYPES = new Set(['user', 'role', 'unit', 'title']);

function toStringId(ref) {
    if (ref == null) return null;
    if (typeof ref === 'object' && ref._id != null) return String(ref._id);
    if (typeof ref === 'object' && ref.id != null) return String(ref.id);
    return String(ref);
}

/**
 * @param {unknown[]} grants
 * @returns {Array<{ type: string, value: string, label?: string }>}
 */
export function normalizeAccessGrants(grants) {
    if (!Array.isArray(grants)) return [];
    const out = [];
    for (const g of grants) {
        const type = String(g?.type || '').toLowerCase();
        if (!GRANT_TYPES.has(type)) continue;
        const value = String(g?.value || '').trim();
        if (!value) continue;
        out.push({
            type,
            value,
            label: g?.label ? String(g.label) : undefined,
        });
    }
    return out;
}

/**
 * @param {object|null|undefined} user
 * @param {unknown} tenantId
 * @returns {{ userId: string|null, roleIds: string[], unitId: string|null, title: string|null }}
 */
export function buildAccessContextFromUser(user, tenantId) {
    const userId = toStringId(user?._id || user?.id);
    let roleIds = [];
    let unitId = null;
    const title = user?.title ? String(user.title) : null;

    const memberships = user?.memberships;
    if (Array.isArray(memberships) && tenantId) {
        const membership = memberships.find((m) => {
            const tId = m?.tenant?.id || m?.tenant?._id || m?.tenant;
            return String(tId) === String(tenantId);
        });
        if (membership?.unit) {
            unitId = toStringId(membership.unit);
        }
        const tenantRoles = membership?.roles;
        if (Array.isArray(tenantRoles) && tenantRoles.length) {
            roleIds = tenantRoles.map((r) => toStringId(r)).filter(Boolean);
        } else if (membership?.role) {
            const rid = toStringId(membership.role?._id || membership.role);
            if (rid) roleIds = [rid];
        }
    }

    return { userId, roleIds, unitId, title };
}

/**
 * @param {{ userId?: string|null, roleIds?: string[], unitId?: string|null, title?: string|null }} ctx
 * @param {Array<{ type: string, value: string }>} grants
 */
export function userMatchesAccessGrants(ctx, grants) {
    if (!grants?.length || !ctx) return false;
    const uid = ctx.userId ? String(ctx.userId) : null;
    const roleSet = new Set((ctx.roleIds || []).map(String));
    const unitId = ctx.unitId ? String(ctx.unitId) : null;
    const title = ctx.title ? String(ctx.title) : null;

    for (const g of grants) {
        const type = String(g?.type || '').toLowerCase();
        const value = String(g?.value || '');
        if (!value) continue;
        if (type === 'user' && uid && value === uid) return true;
        if (type === 'role' && roleSet.has(value)) return true;
        if (type === 'unit' && unitId && value === unitId) return true;
        if (type === 'title' && title && value === title) return true;
    }
    return false;
}

/**
 * Flat pool editor list → REST assign body.
 * @param {unknown[]} items
 * @returns {{ assignedTo?: string, assignedUserIds?: string[], assignedToPool?: object[] }|null}
 */
export function resolveAssignPayloadFromPoolList(items) {
    const normalized = normalizeAccessGrants(items);
    if (!normalized.length) return null;

    const hasStructural = normalized.some((i) => i.type !== 'user');
    if (!hasStructural) {
        const userIds = normalized.map((i) => i.value).filter(Boolean);
        if (userIds.length === 1) return { assignedTo: userIds[0] };
        if (userIds.length > 1) return { assignedUserIds: userIds };
        return null;
    }
    return { assignedToPool: normalized };
}
